import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Heart, MagnifyingGlass, ChatCircleDots, MapPin, Package } from "@phosphor-icons/react";
import SubscribeBox from "@/components/SubscribeBox";
import TestimonialsBand from "@/components/TestimonialsBand";
import PressBand from "@/components/PressBand";
import NewsBanner from "@/components/NewsBanner";

const features = [
  { icon: Heart, title: "Enregistrez vos doudous", text: "Créez une fiche pour chaque doudou avec photo, prénom, description et département." },
  { icon: MapPin, title: "Carte interactive", text: "Déclarez un doudou perdu et placez sa dernière position connue sur la carte." },
  { icon: ChatCircleDots, title: "Chat anonyme", text: "Discutez en toute sécurité avec la personne qui a retrouvé le doudou (prénoms uniquement)." },
  { icon: Package, title: "Envoi par point relais", text: "Adresses privées : organisez le retour via un point relais en quelques clics." },
  { icon: MagnifyingGlass, title: "Pas de compte requis pour signaler", text: "Toute personne peut déclarer un doudou trouvé, sans inscription." },
];

export default function Landing() {
  return (
    <Layout>
      <SEO description="Plateforme française gratuite d'entraide pour retrouver les doudous perdus de vos enfants. Fiches doudou, carte interactive, chat anonyme." />
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-100 text-rose-700 font-bold text-xs tracking-widest uppercase mb-6" data-testid="hero-badge">
              <Heart weight="fill" size={14} /> Pour les familles
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-stone-800 leading-tight" data-testid="hero-title">
              Retrouvez le doudou perdu <span className="text-stone-600 font-black">de votre enfant</span>, <span className="text-orange-500">vite</span> et avec <span className="text-rose-500">douceur</span>.
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
              Doudou Alert aide les familles à protéger les doudous de leurs enfants : fiches sécurisées
              et chat anonyme avec les personnes qui les retrouvent.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/inscription" data-testid="hero-cta-signup">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-8 py-6 text-base shadow-lg shadow-orange-500/30">
                  Créer mon compte gratuit
                </Button>
              </Link>
              <Link to="/trouve" data-testid="hero-cta-found">
                <Button variant="outline" className="rounded-full px-8 py-6 text-base border-2 border-rose-300 text-rose-700 hover:bg-rose-50 font-bold">
                  J'ai trouvé un doudou
                </Button>
              </Link>
              <Link to="/j-ai-perdu-un-doudou" data-testid="hero-cta-lost">
                <Button variant="outline" className="rounded-full px-8 py-6 text-base border-2 border-orange-300 text-orange-700 hover:bg-orange-50 font-bold">
                  J'ai perdu un doudou
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-stone-500">
              <div className="flex -space-x-2">
                {["#fda4af", "#fb923c", "#f43f5e"].map((c) => (
                  <div key={c} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: c }} />
                ))}
              </div>
              <span>Des centaines de doudous en sécurité</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-rose-200/40 to-orange-200/40 rounded-[3rem] blur-2xl" />
            <div className="relative bg-white rounded-[2.5rem] p-10 shadow-2xl shadow-rose-200/40 border border-rose-100">
              <Logo size={120} withText={false} className="justify-center" />
              <div className="mt-6 text-center">
                <p className="font-display text-2xl font-black text-stone-800">Doudou lapin</p>
                <p className="text-sm text-stone-500 mt-1">Lapin rose — Département 31</p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span> À la maison
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <NewsBanner />

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Comment ça marche</span>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-3">
            Pensé pour les parents, et pour les doudous
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-white rounded-3xl p-7 border border-rose-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(249,115,22,0.08)] transition-all"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 grid place-items-center mb-4">
                <f.icon size={26} weight="duotone" />
              </div>
              <h3 className="font-display text-xl font-bold text-stone-800">{f.title}</h3>
              <p className="text-stone-600 mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Subscribe box */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-6">
        <SubscribeBox />
      </section>

      {/* Testimonials */}
      <TestimonialsBand />

      {/* Press mentions */}
      <PressBand />
      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-orange-500 to-rose-500 p-12 md:p-16 text-white text-center shadow-2xl shadow-orange-300/40">
          <h2 className="font-display text-3xl md:text-4xl font-black">Protégez le doudou préféré de votre enfant</h2>
          <p className="mt-4 text-white/90 max-w-2xl mx-auto">Quelques minutes pour créer la fiche. Une vie pour ce compagnon irremplaçable.</p>
          <Link to="/inscription" data-testid="cta-final-signup">
            <Button className="mt-8 bg-white text-orange-600 hover:bg-stone-50 font-bold rounded-full px-8 py-6 text-base shadow-xl">
              Commencer gratuitement
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
